import type {TransactionalMessageBanner as TransactionalMessageBannerType} from '@github-ui/code-view-types'
import type {CommandPaletteDotcomBootstrap} from '@github-ui/command-palette-core/contracts'
import type {ContextRegionPayload} from '@github-ui/global-navigation-types'
import type {NormalizedSequenceString} from '@github-ui/hotkey'
import {TransactionalMessageBanners} from '@github-ui/in-product-targeting/transactional-message-banner/TransactionalMessageBanners'
import {NavigableRefProvider} from '@github-ui/navigable-ref'
import {getKeybindings} from '@github-ui/ui-commands'
import {GitPullRequestIcon, IssueOpenedIcon, RepoIcon} from '@primer/octicons-react'
import {Stack} from '@primer/react'
import {clsx} from 'clsx'
import {lazy, Suspense, useMemo, useState, useSyncExternalStore} from 'react'

import {useContextRegionRefresh} from '../context-region/use-context-region-refresh'
import type {GlobalNavMenuItem, WithShow} from '../menu/GlobalNavMenu'
import {ItemDivider} from '../primitives/ItemDivider'
import {NavLinkItem} from '../primitives/NavItemLink'
import {TopNav, TopNavProvider} from '../primitives/TopNav'
import type {GlobalNavUserMenuProps} from '../user-menu/types'
import {GlobalNavAnalyticsProvider} from '../utils/Analytics'
import {DeferredNavPayloadProvider} from '../utils/DeferredNavPayloadContext'
import {HideOnError} from '../utils/HideOnError'
import styles from './GlobalNavBar.module.css'
import {ContextRegion, useRegisterContextRegion} from './items/ContextRegion'
import {CopilotItems, type CopilotItemsProps} from './items/CopilotItems'
import {EnterpriseBar, type EnterpriseBarProps} from './items/EnterpriseBar'
import {HeaderLogo, type HeaderLogoProps} from './items/HeaderLogo'
import {NotificationItem, type NotificationItemProps} from './items/Notification'
import {Search, type SearchProps} from './items/Search'
import {LazyAccountSwitchDialog} from './LazyAccountSwitchDialog'
import {LazyGlobalCreateMenu} from './LazyGlobalCreateMenu'
import {LazyAnchoredGlobalNavMenu} from './LazyGlobalNavMenu'
import {LazyAnchoredGlobalNavUserMenu} from './LazyGlobalNavUserMenu'
import {LocalNavigation} from './LocalNavigation'

type LinkItemProps = Partial<WithShow<Pick<GlobalNavMenuItem, 'href' | 'commandId'>>>
export interface GlobalNavBarProps {
  /** Identity for the right, user menu */
  owner?: GlobalNavUserMenuProps['owner'] | null

  /** Information about the user's notifications */
  notifications?: NotificationItemProps

  /** Props for the main header logo */
  headerLogo?: HeaderLogoProps

  /** Config options for the issues menu bar link */
  issues?: LinkItemProps

  /** Config options for the pull requests menu bar link */
  pulls?: LinkItemProps

  /** Config options for the contributed repositories menu bar link */
  contributedRepos?: LinkItemProps

  /** Context region data with breadcrumb navigation */
  contextRegion?: ContextRegionPayload

  /** Config options for the copilot menu and buttons */
  copilot?: CopilotItemsProps

  /** Props for the enterprise bar shown above the nav bar */
  enterpriseBar?: EnterpriseBarProps

  /** Props for the search buttons */
  search?: SearchProps

  /** Initial authorized command palette data for this document. */
  commandPalette?: CommandPaletteDotcomBootstrap | null

  /** Current UI-service route, used to invalidate a cached shell bootstrap. */
  routePath?: string

  /** Global transactional message banners to display below the nav bar. */
  globalTransactionalMessage?: TransactionalMessageBannerType[] | null

  /** URL for fetching payload data */
  payloadsUrl?: string | null

  /** URL for fetching route-specific context after a soft navigation */
  contextRegionUrl?: string | null
}

export function GlobalNavBar(props: GlobalNavBarProps) {
  const {
    owner,
    headerLogo,
    copilot,
    notifications,
    contextRegion,
    search,
    commandPalette,
    routePath,
    enterpriseBar,
    issues,
    pulls,
    contributedRepos,
    globalTransactionalMessage,
    payloadsUrl,
    contextRegionUrl,
  } = props
  const viewerIsLoggedIn = owner != null
  const [commandPaletteReady, setCommandPaletteReady] = useState(false)
  const {activeContextRegion} = useContextRegionRefresh({
    contextRegion,
    contextRegionUrl,
  })

  const hasLocalNavigation = !!activeContextRegion?.localNavigation && activeContextRegion.localNavigation.length > 0
  const contextRegionProps = useRegisterContextRegion()

  const localNavigationKeybindings = useMemo(
    () =>
      new Set(
        activeContextRegion?.localNavigation?.flatMap(item => (item.commandId ? getKeybindings(item.commandId) : [])) ??
          [],
      ),
    [activeContextRegion?.localNavigation],
  )

  return (
    <NavigableRefProvider>
      <GlobalNavAnalyticsProvider category="GLOBAL_NAV_BAR">
        <TopNavProvider
          hasLocalNavigation={hasLocalNavigation}
          isSearchPage={search?.isSearchPage ?? false}
          isJumpToSearch={search?.isJumpToSearch ?? false}
          payloadsUrl={payloadsUrl}
        >
          <DeferredNavPayloadProvider>
            {viewerIsLoggedIn && commandPalette && (
              <HideOnError>
                <CommandPaletteNavHost
                  payload={commandPalette}
                  routePath={routePath}
                  onReady={setCommandPaletteReady}
                />
              </HideOnError>
            )}
            <TopNav>
              <TopNav.Above>
                <EnterpriseBar {...enterpriseBar} />
              </TopNav.Above>

              {viewerIsLoggedIn && (
                <TopNav.Left>
                  <>
                    <HideOnError>
                      <LazyAnchoredGlobalNavMenu />
                    </HideOnError>
                    <HeaderLogo {...headerLogo} />
                  </>
                </TopNav.Left>
              )}

              <TopNav.Center
                className={clsx(
                  !activeContextRegion?.crumbs?.length && styles.noBreadcrumbs,
                  !viewerIsLoggedIn && styles.loggedOut,
                )}
              >
                <HideOnError>
                  <ContextRegion
                    {...contextRegionProps}
                    crumbs={activeContextRegion?.crumbs}
                    viewerIsLoggedIn={viewerIsLoggedIn}
                    currentPath={activeContextRegion?.currentPath}
                    selectedLink={activeContextRegion?.selectedLink}
                  />
                </HideOnError>
                {viewerIsLoggedIn && (
                  <HideOnError>
                    <Search
                      {...search}
                      commandPaletteDisabled={Boolean(commandPalette && !commandPaletteReady)}
                      commandPaletteHotkey={commandPalette?.host.activationHotkey}
                    />
                  </HideOnError>
                )}
              </TopNav.Center>

              <TopNav.Right>
                {viewerIsLoggedIn && (
                  <>
                    <Stack
                      data-testid="top-bar-actions"
                      direction="horizontal"
                      align="center"
                      gap="condensed"
                      padding="none"
                    >
                      <HideOnError>
                        <CopilotItems {...copilot} />
                      </HideOnError>

                      <ItemDivider />

                      <HideOnError>
                        <LazyGlobalCreateMenu className="hide-sm" />
                      </HideOnError>

                      <GlobalNavBarLinkItems
                        issues={issues}
                        pulls={pulls}
                        contributedRepos={contributedRepos}
                        localNavKeybindings={localNavigationKeybindings}
                      />
                    </Stack>
                    <NotificationItem {...notifications} />
                    {owner && (
                      <HideOnError>
                        <LazyAnchoredGlobalNavUserMenu owner={owner} />
                      </HideOnError>
                    )}
                    {owner && <LazyAccountSwitchDialog />}
                  </>
                )}
              </TopNav.Right>

              {hasLocalNavigation && (
                <HideOnError>
                  <LocalNavigation {...activeContextRegion} />
                </HideOnError>
              )}
            </TopNav>
            {globalTransactionalMessage?.length ? (
              <HideOnError>
                <TransactionalMessageBanners banners={globalTransactionalMessage} displayStyle="full-bleed" />
              </HideOnError>
            ) : null}
          </DeferredNavPayloadProvider>
        </TopNavProvider>
      </GlobalNavAnalyticsProvider>
    </NavigableRefProvider>
  )
}

// The host validates URLs against the browser origin, which is not available during nav SSR.
const subscribeToHydration = () => () => {}
const clientHydrated = () => true
const serverHydrated = () => false
const CommandPaletteDotcom = lazy(async () => {
  const module = await import('@github-ui/command-palette/dotcom')
  return {default: module.CommandPaletteDotcom}
})

function CommandPaletteNavHost({
  payload,
  routePath,
  onReady,
}: {
  payload: CommandPaletteDotcomBootstrap
  routePath?: string
  onReady: (ready: boolean) => void
}) {
  const hydrated = useSyncExternalStore(subscribeToHydration, clientHydrated, serverHydrated)
  return hydrated ? (
    <Suspense fallback={null}>
      <CommandPaletteDotcom {...payload} routePath={routePath} onReady={onReady} />
    </Suspense>
  ) : null
}

function GlobalNavBarLinkItems({
  localNavKeybindings,
  ...props
}: Pick<GlobalNavBarProps, keyof typeof GlobalNavBarItems> & {localNavKeybindings: Set<NormalizedSequenceString>}) {
  return (
    <Stack direction="horizontal" align="center" gap="condensed" padding="none" className="hide-sm hide-md">
      {(['issues', 'pulls', 'contributedRepos'] as const).map(key => {
        const {label, ...navItemProps} = GlobalNavBarItems[key]

        const {show, ...mergedProps} = {...navItemProps, ...props[key]}

        if (show === false) {
          return null
        }

        // Suppress this global nav command if a local nav item has the same keybinding. This is a hack because we are
        // bound by legacy support for the same keybinding across the global and local "go to issues/PRs". UI commands
        // doesn't (yet) support a concept of 'priority' for global commands, so we just don't bind the command at all.
        const keybindings = getKeybindings(mergedProps.commandId)
        const hasConflictWithLocalNav = keybindings.some(kb => localNavKeybindings.has(kb))
        const withCommandIdOverride = {...mergedProps, ...(hasConflictWithLocalNav ? {commandId: undefined} : {})}

        return <NavLinkItem key={key} {...withCommandIdOverride} aria-label={label} />
      })}
    </Stack>
  )
}

/**
 * The items in the global navigation bar
 * Any config set here will be the default, and can be overridden by passing props to the GlobalNavBar component
 */
export const GlobalNavBarItems = {
  issues: {
    href: '/issues',
    icon: IssueOpenedIcon,
    label: 'All issues',
    analyticsKey: 'ISSUES_HEADER',
    show: true,
    commandId: 'global-navigation:go-to-issues',
  },
  pulls: {
    href: '/pulls',
    icon: GitPullRequestIcon,
    label: 'All pull requests',
    analyticsKey: 'PULL_REQUESTS_HEADER',
    show: true,
    commandId: 'global-navigation:go-to-pull-requests',
  },
  contributedRepos: {
    href: '/repos',
    label: 'All repositories',
    analyticsKey: 'REPOS_HEADER',
    icon: RepoIcon,
    show: true,
    commandId: 'global-navigation:go-to-repositories',
  },
} as const satisfies Record<string, WithShow<Omit<GlobalNavMenuItem, 'key'>>>
